import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { normalizeId } from './useSupabase';
import { getContractedHoursForMonthFromRows } from './useUserRoleHistory';
import {
  getISOWeekMonday,
  getISOWeekDates,
  getISOWeekNumber,
  getUserTimeOffDates,
  prorateWeeklyContracted,
  getCompletionStatus,
  getCompletionPct,
  buildWeekDayColumns,
  formatDate,
} from '../utils/timesheetHelpers';

/**
 * Builds a lookup Map: "userId:date" → total seconds for that user on that day.
 */
function buildTimeEntryIndex(timeEntries, startDate, endDate) {
  const index = new Map(); // "userId:date" → totalSeconds
  const projectIndex = new Map(); // "userId:date:projectId" → totalSeconds
  for (const e of timeEntries) {
    if (!e.date || e.date < startDate || e.date > endDate) continue;
    const uid = normalizeId(e.userId);
    const key = `${uid}:${e.date}`;
    index.set(key, (index.get(key) || 0) + (e.duration || 0));

    const pKey = `${uid}:${e.date}:${normalizeId(e.projectId)}`;
    projectIndex.set(pKey, (projectIndex.get(pKey) || 0) + (e.duration || 0));
  }
  return { index, projectIndex };
}

/**
 * Converts an ISO week Monday date to ISO week ID (YYYY-Www).
 * Follows the same logic as useHFBData.js getWeekIdFromMonday.
 */
function getWeekIdFromMonday(mondayDateStr) {
  if (!mondayDateStr) return null;
  const d = new Date(mondayDateStr + 'T00:00:00Z');
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return d.getUTCFullYear() + '-W' + String(weekNum).padStart(2, '0');
}

/**
 * Builds a lookup Map from HFB allocation rows (weekly_project_allocations).
 * Input rows: { user_id, project_id, week_id, percentage, notes }
 * Converts percentage → hours via (percentage / 100) * 40, matching useHFBData.js line 363.
 */
function buildAllocationIndex(hfbRows, weekMondays) {
  const index = new Map(); // "userId:weekStart" → hours
  const projectIndex = new Map(); // "userId:weekStart:projectId" → hours

  // Build a weekId → weekStart (Monday) reverse map
  const weekIdToMonday = new Map();
  for (const monday of weekMondays) {
    const wid = getWeekIdFromMonday(monday);
    if (wid) weekIdToMonday.set(wid, monday);
  }

  for (const row of hfbRows) {
    const monday = weekIdToMonday.get(row.week_id);
    if (!monday) continue;

    const uid = normalizeId(row.user_id);
    const pid = normalizeId(row.project_id);
    const hours = ((row.percentage || 0) / 100) * 40;

    const key = `${uid}:${monday}`;
    index.set(key, (index.get(key) || 0) + hours);

    const pKey = `${uid}:${monday}:${pid}`;
    projectIndex.set(pKey, (projectIndex.get(pKey) || 0) + hours);
  }
  return { index, projectIndex };
}

/**
 * Returns the set of project IDs a user logged time to in a date range.
 */
function getUserProjectIds(timeEntries, userId, startDate, endDate) {
  const projectIds = new Set();
  const uid = normalizeId(userId);
  for (const e of timeEntries) {
    if (normalizeId(e.userId) !== uid) continue;
    if (!e.date || e.date < startDate || e.date > endDate) continue;
    projectIds.add(normalizeId(e.projectId));
  }
  return projectIds;
}

/**
 * Main hook for Timesheet Overview page.
 *
 * @param {object} params
 * @param {Array} params.users - activeUsers
 * @param {Array} params.timeEntries - activeTimeEntries
 * @param {boolean} params.useSupabaseDB - whether Supabase mode is active
 * @param {Array} params.teams - activeTeams
 * @param {Array} params.holidays - activeHolidays
 * @param {Array} params.timeOff - activeTimeOff
 * @param {Array} params.projects - activeProjects
 * @param {object} params.currentUser - { id, role }
 * @param {string} params.startDate - YYYY-MM-DD
 * @param {string} params.endDate - YYYY-MM-DD
 * @param {'weekly'|'monthly'} params.period
 * @param {'team'|'project'} params.groupBy
 * @param {number|null} params.filterTeamId
 * @param {number|null} params.filterUserId
 * @param {number|null} params.filterProjectId
 * @param {boolean} params.incompleteOnly
 * @param {boolean} params.varianceOnly
 */
export function useTimesheetOverview({
  users = [],
  timeEntries = [],
  useSupabaseDB = false,
  teams = [],
  holidays = [],
  timeOff = [],
  projects = [],
  currentUser,
  startDate,
  endDate,
  period = 'weekly',
  groupBy = 'team',
  filterTeamId = null,
  filterUserId = null,
  filterProjectId = null,
  incompleteOnly = false,
  varianceOnly = false,
  getProjectIdsForTeam = null,
}) {
  // ──────────────────────────────────────────────────────────
  // Fetch HFB allocations from weekly_project_allocations
  // (the primary allocation system — NOT tt_allocations).
  // Follows the useHFBProjectAllocations pattern from useHFBData.js.
  // ──────────────────────────────────────────────────────────
  const [hfbAllocations, setHfbAllocations] = useState([]);
  // Single counter tracks all in-flight fetches; isLoading is false only when count reaches 0.
  // This prevents three independent booleans from going false prematurely when concurrent
  // fetches overlap (e.g. stale fetch's finally fires while a newer fetch is still running).
  const [loadingCount, setLoadingCount] = useState(0);
  const lastFetchKeyRef = useRef(null);

  // Compute week Mondays in the selected range (needed for allocation query)
  const weekMondays = useMemo(() => {
    if (!startDate || !endDate) return [];
    const mondays = [];
    let current = getISOWeekMonday(startDate);
    const lastMonday = getISOWeekMonday(endDate);
    const currentDate = new Date(current + 'T00:00:00');
    const endMon = new Date(lastMonday + 'T00:00:00');
    while (currentDate <= endMon) {
      mondays.push(formatDate(currentDate));
      currentDate.setDate(currentDate.getDate() + 7);
    }
    return mondays;
  }, [startDate, endDate]);

  // ISO week IDs for the query
  const weekIds = useMemo(() => {
    return weekMondays.map(getWeekIdFromMonday).filter(Boolean);
  }, [weekMondays]);

  const fetchHfbAllocations = useCallback(async () => {
    if (!useSupabaseDB || weekIds.length === 0) {
      setHfbAllocations([]);
      return;
    }

    const fetchKey = weekIds.join(',');
    if (fetchKey === lastFetchKeyRef.current) return;

    setLoadingCount(c => c + 1);
    try {
      const { data, error } = await supabase
        .from('weekly_project_allocations')
        .select('id, week_id, user_id, project_id, percentage, notes')
        .in('week_id', weekIds);

      if (error) {
        console.error('Timesheet allocation fetch error:', error);
        setHfbAllocations([]);
      } else {
        setHfbAllocations(data || []);
        lastFetchKeyRef.current = fetchKey;
      }
    } catch (err) {
      console.error('Timesheet allocation fetch error:', err);
      setHfbAllocations([]);
    } finally {
      setLoadingCount(c => Math.max(0, c - 1));
    }
  }, [useSupabaseDB, weekIds]);

  useEffect(() => {
    fetchHfbAllocations();
  }, [fetchHfbAllocations]);

  // ──────────────────────────────────────────────────────────
  // Fetch user_role_history for all visible users.
  // Source of truth for contracted hours per user per period.
  // ──────────────────────────────────────────────────────────
  const [roleHistoryRows, setRoleHistoryRows] = useState([]);
  const lastRHFetchKeyRef = useRef(null);

  // User IDs that logged at least one time entry within the selected range.
  // Used to keep employees who are inactive *now* visible when they were
  // active during the selected period (e.g. someone who left last month must
  // still show up when viewing that month).
  const userIdsWithEntriesInRange = useMemo(() => {
    const set = new Set();
    if (!startDate || !endDate) return set;
    for (const e of timeEntries) {
      if (!e.date || e.date < startDate || e.date > endDate) continue;
      set.add(normalizeId(e.userId));
    }
    return set;
  }, [timeEntries, startDate, endDate]);

  // A user is relevant to the current view if they are currently active, OR
  // they are inactive now but logged time during the selected period.
  const isUserRelevantForRange = useCallback((u) => {
    if (u.isActive !== false) return true;
    return userIdsWithEntriesInRange.has(normalizeId(u.id));
  }, [userIdsWithEntriesInRange]);

  const visibleUserIds = useMemo(() => {
    return users.filter(isUserRelevantForRange).map(u => normalizeId(u.id));
  }, [users, isUserRelevantForRange]);

  const fetchRoleHistory = useCallback(async () => {
    if (!useSupabaseDB || visibleUserIds.length === 0) {
      setRoleHistoryRows([]);
      return;
    }

    const fetchKey = visibleUserIds.join(',');
    if (fetchKey === lastRHFetchKeyRef.current) return;

    setLoadingCount(c => c + 1);
    try {
      const { data, error } = await supabase
        .from('user_role_history')
        .select('user_id, effective_from, effective_to, contracted_hours')
        .in('user_id', visibleUserIds);

      if (error) {
        console.error('Timesheet role history fetch error:', error);
        setRoleHistoryRows([]);
      } else {
        setRoleHistoryRows(data || []);
        lastRHFetchKeyRef.current = fetchKey;
      }
    } catch (err) {
      console.error('Timesheet role history fetch error:', err);
      setRoleHistoryRows([]);
    } finally {
      setLoadingCount(c => Math.max(0, c - 1));
    }
  }, [useSupabaseDB, visibleUserIds]);

  useEffect(() => {
    fetchRoleHistory();
  }, [fetchRoleHistory]);

  // Build a lookup: userId → array of role history rows
  const roleHistoryByUser = useMemo(() => {
    const map = new Map();
    for (const row of roleHistoryRows) {
      const uid = normalizeId(row.user_id);
      if (!map.has(uid)) map.set(uid, []);
      map.get(uid).push(row);
    }
    return map;
  }, [roleHistoryRows]);

  // Office-aware holiday sets.
  // A holiday applies to a user if: holiday.office is null (all offices)
  // OR holiday.office matches the user's officeLocation.
  const holidaySets = useMemo(() => {
    const allSet = new Set();    // all holidays (for column headers)
    const nlSet = new Set();     // holidays for NL users
    const inSet = new Set();     // holidays for IN users
    for (const h of holidays) {
      if (!h.date) continue;
      allSet.add(h.date);
      if (!h.office || h.office === 'NL') nlSet.add(h.date);
      if (!h.office || h.office === 'IN') inSet.add(h.date);
    }
    return { all: allSet, NL: nlSet, IN: inSet };
  }, [holidays]);

  // Helper: get the correct holiday set for a user's office
  const getHolidaySetForUser = (user) => {
    const office = (user.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';
    return holidaySets[office] || holidaySets.all;
  };

  // Approved time-off per user: Map<userId, Set<date>>
  const timeOffByUser = useMemo(() => {
    const map = new Map();
    // Include non-rejected leave that means the user is NOT working.
    // WFH does not reduce the target — the user still logs time.
    const ABSENCE_TYPES = new Set(['vacation', 'sick', 'personal']);
    const activeLeave = timeOff.filter(r =>
      r.status !== 'rejected'
      && ABSENCE_TYPES.has(r.type)
    );
    for (const r of activeLeave) {
      const uid = normalizeId(r.userId);
      if (!map.has(uid)) map.set(uid, []);
      map.get(uid).push(r);
    }
    // Convert to date sets
    const result = new Map();
    for (const [uid, records] of map) {
      result.set(uid, getUserTimeOffDates(records));
    }
    return result;
  }, [timeOff]);

  // ──────────────────────────────────────────────────────────
  // Fetch HFB team assignments (weekly_team_assignments) so
  // we can resolve each user's team for the selected week(s).
  // Falls back to users.teamId when no HFB assignment exists.
  // ──────────────────────────────────────────────────────────
  const [hfbTeamAssignments, setHfbTeamAssignments] = useState([]);
  const lastTeamFetchKeyRef = useRef(null);

  const fetchHfbTeamAssignments = useCallback(async () => {
    if (!useSupabaseDB || weekIds.length === 0) {
      setHfbTeamAssignments([]);
      return;
    }

    const fetchKey = 'ta:' + weekIds.join(',');
    if (fetchKey === lastTeamFetchKeyRef.current) return;

    setLoadingCount(c => c + 1);
    try {
      const { data, error } = await supabase
        .from('weekly_team_assignments')
        .select('user_id, team_id, week_id')
        .in('week_id', weekIds);

      if (error) {
        console.error('Timesheet team assignment fetch error:', error);
        setHfbTeamAssignments([]);
      } else {
        setHfbTeamAssignments(data || []);
        lastTeamFetchKeyRef.current = fetchKey;
      }
    } catch (err) {
      console.error('Timesheet team assignment fetch error:', err);
      setHfbTeamAssignments([]);
    } finally {
      setLoadingCount(c => Math.max(0, c - 1));
    }
  }, [useSupabaseDB, weekIds]);

  useEffect(() => {
    fetchHfbTeamAssignments();
  }, [fetchHfbTeamAssignments]);

  const isLoading = loadingCount > 0;

  const refresh = useCallback(() => {
    lastFetchKeyRef.current = null;
    lastRHFetchKeyRef.current = null;
    lastTeamFetchKeyRef.current = null;
    fetchHfbAllocations();
    fetchRoleHistory();
    fetchHfbTeamAssignments();
  }, [fetchHfbAllocations, fetchRoleHistory, fetchHfbTeamAssignments]);

  // Resolve each user's effective team: HFB assignment (most recent week) > users.teamId
  const userEffectiveTeam = useMemo(() => {
    const map = new Map(); // userId → teamId
    // Start with fallback from users table
    for (const u of users) {
      if (u.teamId) map.set(normalizeId(u.id), normalizeId(u.teamId));
    }
    // Override with HFB assignments (sorted by week_id so latest wins)
    const sorted = [...hfbTeamAssignments].sort((a, b) => (a.week_id || '').localeCompare(b.week_id || ''));
    for (const a of sorted) {
      map.set(normalizeId(a.user_id), normalizeId(a.team_id));
    }
    return map;
  }, [users, hfbTeamAssignments]);

  // Supervisor scoping: filter users to supervised teams
  const visibleUsers = useMemo(() => {
    let filtered = users.filter(isUserRelevantForRange);

    // Supervisor sees only their teams
    if (currentUser?.role === 'supervisor') {
      const supervisedTeamIds = new Set();
      const curId = normalizeId(currentUser.id);
      for (const t of teams) {
        if (t.supervisorIds && t.supervisorIds.map(normalizeId).includes(curId)) {
          supervisedTeamIds.add(normalizeId(t.id));
        }
      }
      filtered = filtered.filter(u => {
        const effectiveTeam = userEffectiveTeam.get(normalizeId(u.id));
        return effectiveTeam && supervisedTeamIds.has(effectiveTeam);
      });
    }

    // Apply team filter using effective team (HFB > fallback)
    if (filterTeamId) {
      const ftid = normalizeId(filterTeamId);
      filtered = filtered.filter(u => userEffectiveTeam.get(normalizeId(u.id)) === ftid);
    }

    // Apply user filter
    if (filterUserId) {
      filtered = filtered.filter(u => normalizeId(u.id) === normalizeId(filterUserId));
    }

    return filtered;
  }, [users, currentUser, teams, filterTeamId, filterUserId, userEffectiveTeam, isUserRelevantForRange]);

  // Time entry and allocation indexes
  const { teIndex, teProjectIndex } = useMemo(() => {
    const { index, projectIndex } = buildTimeEntryIndex(timeEntries, startDate, endDate);
    return { teIndex: index, teProjectIndex: projectIndex };
  }, [timeEntries, startDate, endDate]);

  const { allocIndex, allocProjectIndex } = useMemo(() => {
    const { index, projectIndex } = buildAllocationIndex(hfbAllocations, weekMondays);
    return { allocIndex: index, allocProjectIndex: projectIndex };
  }, [hfbAllocations, weekMondays]);

  // Project lookup map
  const projectMap = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      map.set(normalizeId(p.id), p);
    }
    return map;
  }, [projects]);

  // Team lookup map
  const teamMap = useMemo(() => {
    const map = new Map();
    for (const t of teams) {
      map.set(normalizeId(t.id), t);
    }
    return map;
  }, [teams]);

  // All dates in the selected range, for the daily (weekly period) view
  const allDates = useMemo(() => {
    if (weekMondays.length === 0 || !startDate || !endDate) return [];
    const dates = [];
    for (const monday of weekMondays) {
      for (const date of getISOWeekDates(monday)) {
        if (date >= startDate && date <= endDate) dates.push(date);
      }
    }
    return dates;
  }, [weekMondays, startDate, endDate]);

  // Day columns for daily view — covers every day in the selected range
  const dayColumns = useMemo(() => {
    if (weekMondays.length === 0) return [];
    const allCols = [];
    for (const monday of weekMondays) {
      for (const col of buildWeekDayColumns(monday, holidaySets.all)) {
        if (col.date >= startDate && col.date <= endDate) allCols.push(col);
      }
    }
    return allCols;
  }, [weekMondays, holidaySets, startDate, endDate]);

  // Week columns for monthly view
  const weekColumns = useMemo(() => {
    return weekMondays.map(monday => {
      const dates = getISOWeekDates(monday);
      const weekNum = getISOWeekNumber(monday);
      const dayNum = parseInt(monday.slice(8), 10);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIdx = new Date(monday + 'T00:00:00').getMonth();
      return {
        weekStart: monday,
        weekEnd: dates[6],
        label: `W${weekNum} (${dayNum} ${monthNames[monthIdx]})`,
      };
    });
  }, [weekMondays]);

  // Build row data for team member view
  const teamMemberRows = useMemo(() => {
    if (groupBy !== 'team') return [];

    return visibleUsers.map(user => {
      const uid = normalizeId(user.id);
      const userTimeOff = timeOffByUser.get(uid) || new Set();
      const userHolidays = getHolidaySetForUser(user);
      const effectiveTeamId = userEffectiveTeam.get(uid) || normalizeId(user.teamId);
      const team = teamMap.get(effectiveTeamId);

      if (period === 'weekly') {
        // Day-level detail across the entire selected range
        if (allDates.length === 0) return null;

        const dailyHours = allDates.map(date => {
          const secs = teIndex.get(`${uid}:${date}`) || 0;
          return secs / 3600;
        });

        const weekLogged = dailyHours.reduce((s, h) => s + h, 0);

        // Sum allocation and contracted hours across all weeks in range
        let weekAllocated = 0;
        for (const monday of weekMondays) {
          weekAllocated += allocIndex.get(`${uid}:${monday}`) || 0;
        }

        const userRH = roleHistoryByUser.get(uid) || [];
        // Use first week for base contracted hours display
        const resolvedContracted = getContractedHoursForMonthFromRows(userRH, weekMondays[0], 0);
        let weekContracted = 0;
        for (const monday of weekMondays) {
          const rhContracted = getContractedHoursForMonthFromRows(userRH, monday, 0);
          const userWithResolved = { ...user, contractedHours: rhContracted };
          weekContracted += prorateWeeklyContracted(userWithResolved, monday, userHolidays, userTimeOff);
        }

        const vsAllocated = weekLogged - weekAllocated;
        const vsContracted = weekLogged - weekContracted;
        const status = getCompletionStatus(weekLogged, weekContracted);
        const completionPct = getCompletionPct(weekLogged, weekContracted);

        // Missing days: weekdays where user was active but logged 0
        let missingDays = 0;
        allDates.forEach((dateStr, i) => {
          const dayOfWeek = new Date(dateStr + 'T00:00:00').getDay();
          if (dayOfWeek === 0 || dayOfWeek === 6) return; // skip weekends
          const isActive = (!user.joinDate || dateStr >= user.joinDate)
            && (!user.endDate || dateStr <= user.endDate)
            && !userHolidays.has(dateStr)
            && !userTimeOff.has(dateStr);
          if (isActive && dailyHours[i] === 0) missingDays++;
        });

        // Project breakdown for drilldown — cover full range
        const userProjectIds = getUserProjectIds(timeEntries, uid, startDate, endDate);
        const projectRows = Array.from(userProjectIds).map(projectId => {
          const project = projectMap.get(projectId);
          const projDailyHours = allDates.map(date => {
            const secs = teProjectIndex.get(`${uid}:${date}:${projectId}`) || 0;
            return secs / 3600;
          });
          const projTotal = projDailyHours.reduce((s, h) => s + h, 0);
          let projAllocated = 0;
          for (const monday of weekMondays) {
            projAllocated += allocProjectIndex.get(`${uid}:${monday}:${projectId}`) || 0;
          }
          return {
            type: 'project-detail',
            projectId,
            projectName: project?.name || `Project #${projectId}`,
            projectCode: project?.code || '',
            projectColor: project?.color || '#999',
            dailyHours: projDailyHours,
            total: projTotal,
            allocated: projAllocated,
          };
        }).sort((a, b) => b.total - a.total);

        return {
          type: 'user',
          userId: uid,
          userName: user.name || user.email || `User #${uid}`,
          jobRole: user.effectiveJobRole || user.jobRole || '',
          jobLevel: user.effectiveJobLevel ?? user.jobLevel ?? 0,
          teamId: effectiveTeamId,
          teamName: team?.name || '',
          teamColor: team?.color || '#ccc',
          baseContractedHours: resolvedContracted,
          dailyHours,
          weekLogged,
          weekAllocated,
          weekContracted,
          vsAllocated,
          vsContracted,
          status,
          completionPct,
          missingDays,
          projectRows,
          isInactive: user.isActive === false,
        };
      } else {
        // Monthly: week-level totals
        const weekTotals = weekMondays.map(monday => {
          const dates = getISOWeekDates(monday);
          let total = 0;
          for (const date of dates) {
            total += (teIndex.get(`${uid}:${date}`) || 0) / 3600;
          }
          return total;
        });

        const monthLogged = weekTotals.reduce((s, h) => s + h, 0);

        let monthAllocated = 0;
        for (const monday of weekMondays) {
          monthAllocated += allocIndex.get(`${uid}:${monday}`) || 0;
        }

        // Resolve contracted hours from role history (source of truth)
        const userRH = roleHistoryByUser.get(uid) || [];
        const resolvedContracted = getContractedHoursForMonthFromRows(userRH, startDate, 0);
        // Sum per-week contracted hours across the full range so multi-month
        // periods (last 3 months, custom range) are calculated correctly.
        let monthContracted = 0;
        for (const monday of weekMondays) {
          const rhContracted = getContractedHoursForMonthFromRows(userRH, monday, 0);
          const userWithResolved = { ...user, contractedHours: rhContracted };
          monthContracted += prorateWeeklyContracted(userWithResolved, monday, userHolidays, userTimeOff);
        }

        const vsAllocated = monthLogged - monthAllocated;
        const vsContracted = monthLogged - monthContracted;
        const status = getCompletionStatus(monthLogged, monthContracted);
        const completionPct = getCompletionPct(monthLogged, monthContracted);

        return {
          type: 'user',
          userId: uid,
          userName: user.name || user.email || `User #${uid}`,
          jobRole: user.effectiveJobRole || user.jobRole || '',
          jobLevel: user.effectiveJobLevel ?? user.jobLevel ?? 0,
          teamId: effectiveTeamId,
          teamName: team?.name || '',
          teamColor: team?.color || '#ccc',
          baseContractedHours: resolvedContracted,
          weekTotals,
          monthLogged,
          monthAllocated,
          monthContracted,
          vsAllocated,
          vsContracted,
          status,
          completionPct,
          isInactive: user.isActive === false,
        };
      }
    }).filter(Boolean);
  }, [
    groupBy, period, visibleUsers, weekMondays, allDates, teIndex, teProjectIndex,
    allocIndex, allocProjectIndex, holidaySets, timeOffByUser, teamMap,
    projectMap, timeEntries, startDate, endDate, roleHistoryByUser, userEffectiveTeam,
  ]);

  // Build row data for project view
  // When a team filter is active, restrict to projects assigned to that team via team_projects
  const teamProjectIds = useMemo(() => {
    if (!filterTeamId || !getProjectIdsForTeam) return null;
    return new Set((getProjectIdsForTeam(filterTeamId) || []).map(normalizeId));
  }, [filterTeamId, getProjectIdsForTeam]);

  const projectViewRows = useMemo(() => {
    if (groupBy !== 'project') return [];

    // Collect all projects with logged time in range
    const projectTotals = new Map(); // projectId → { logged, allocated, dailyHours/weekTotals, users }

    for (const user of visibleUsers) {
      const uid = normalizeId(user.id);

      if (period === 'weekly') {
        if (allDates.length === 0) continue;
        const userProjectIds = getUserProjectIds(timeEntries, uid, startDate, endDate);

        for (const pid of userProjectIds) {
          if (filterProjectId && normalizeId(filterProjectId) !== pid) continue;
          if (teamProjectIds && !teamProjectIds.has(pid)) continue;
          if (!projectTotals.has(pid)) {
            projectTotals.set(pid, { dailyHours: new Array(allDates.length).fill(0), total: 0, allocated: 0, userRows: [] });
          }
          const pt = projectTotals.get(pid);
          const projDailyHours = allDates.map(date => {
            const secs = teProjectIndex.get(`${uid}:${date}:${pid}`) || 0;
            return secs / 3600;
          });
          const projTotal = projDailyHours.reduce((s, h) => s + h, 0);
          let projAllocated = 0;
          for (const monday of weekMondays) {
            projAllocated += allocProjectIndex.get(`${uid}:${monday}:${pid}`) || 0;
          }

          projDailyHours.forEach((h, i) => { pt.dailyHours[i] += h; });
          pt.total += projTotal;
          pt.allocated += projAllocated;

          const team = teamMap.get(normalizeId(user.teamId));
          pt.userRows.push({
            type: 'user-detail',
            userId: uid,
            userName: user.name || user.email || `User #${uid}`,
            teamColor: team?.color || '#ccc',
            dailyHours: projDailyHours,
            total: projTotal,
            allocated: projAllocated,
          });
        }
      } else {
        // Monthly project view
        for (const monday of weekMondays) {
          const dates = getISOWeekDates(monday);
          const userProjectIds = getUserProjectIds(timeEntries, uid, monday, dates[6]);

          for (const pid of userProjectIds) {
            if (filterProjectId && normalizeId(filterProjectId) !== pid) continue;
            if (teamProjectIds && !teamProjectIds.has(pid)) continue;
            if (!projectTotals.has(pid)) {
              projectTotals.set(pid, { weekTotals: new Array(weekMondays.length).fill(0), total: 0, allocated: 0, userRows: [] });
            }
            const pt = projectTotals.get(pid);
            const weekIdx = weekMondays.indexOf(monday);
            let weekTotal = 0;
            for (const date of dates) {
              weekTotal += (teProjectIndex.get(`${uid}:${date}:${pid}`) || 0) / 3600;
            }
            if (pt.weekTotals) pt.weekTotals[weekIdx] = (pt.weekTotals[weekIdx] || 0) + weekTotal;
            pt.total += weekTotal;
            pt.allocated += allocProjectIndex.get(`${uid}:${monday}:${pid}`) || 0;
          }
        }
      }
    }

    // Convert to array
    return Array.from(projectTotals.entries()).map(([pid, pt]) => {
      const project = projectMap.get(pid);
      return {
        type: 'project',
        projectId: pid,
        projectName: project?.name || `Project #${pid}`,
        projectCode: project?.code || '',
        projectColor: project?.color || '#999',
        dailyHours: pt.dailyHours,
        weekTotals: pt.weekTotals,
        total: pt.total,
        allocated: pt.allocated,
        userRows: pt.userRows?.sort((a, b) => b.total - a.total) || [],
      };
    }).sort((a, b) => b.total - a.total);
  }, [
    groupBy, period, visibleUsers, weekMondays, allDates, teProjectIndex,
    allocProjectIndex, projectMap, teamMap, timeEntries, startDate, endDate,
    filterProjectId, teamProjectIds,
  ]);

  // Apply project filter to team member rows (filter out project sub-rows)
  const rows = useMemo(() => {
    let result = groupBy === 'team' ? teamMemberRows : projectViewRows;

    // Filter by project (for team view, filter drilldown sub-rows and hide users with no hours on that project)
    if (filterProjectId && groupBy === 'team') {
      const pid = normalizeId(filterProjectId);
      result = result.map(row => ({
        ...row,
        projectRows: row.projectRows?.filter(
          pr => normalizeId(pr.projectId) === pid
        ),
      })).filter(row => {
        // Weekly view has projectRows; monthly view does not
        if (row.projectRows) return row.projectRows.length > 0;
        // Monthly: check time entries directly
        const userProjects = getUserProjectIds(timeEntries, row.userId, startDate, endDate);
        return userProjects.has(pid);
      });
    }

    // Incomplete only filter
    if (incompleteOnly) {
      result = result.filter(row => row.status && row.status !== 'green');
    }

    // Variance only filter
    if (varianceOnly) {
      result = result.filter(row => {
        const va = Math.abs(row.vsAllocated || 0);
        const vc = Math.abs(row.vsContracted || 0);
        return va > 0.5 || vc > 0.5;
      });
    }

    // Sort: by team name, then user name
    if (groupBy === 'team') {
      result = [...result].sort((a, b) => {
        const teamCmp = (a.teamName || '').localeCompare(b.teamName || '');
        if (teamCmp !== 0) return teamCmp;
        return (a.userName || '').localeCompare(b.userName || '');
      });
    }

    return result;
  }, [groupBy, teamMemberRows, projectViewRows, filterProjectId, incompleteOnly, varianceOnly, timeEntries, startDate, endDate]);

  // KPI calculations
  const kpis = useMemo(() => {
    if (groupBy !== 'team' || teamMemberRows.length === 0) {
      return { totalLogged: 0, totalAllocated: 0, totalContracted: 0, completionPct: 0, incompleteCount: 0 };
    }

    let totalLogged = 0;
    let totalAllocated = 0;
    let totalContracted = 0;
    let incompleteCount = 0;

    for (const row of teamMemberRows) {
      const logged = period === 'weekly' ? row.weekLogged : row.monthLogged;
      const allocated = period === 'weekly' ? row.weekAllocated : row.monthAllocated;
      const contracted = period === 'weekly' ? row.weekContracted : row.monthContracted;
      totalLogged += logged || 0;
      totalAllocated += allocated || 0;
      totalContracted += contracted || 0;
      if (row.status !== 'green') incompleteCount++;
    }

    const completionPct = totalContracted > 0
      ? (totalLogged / totalContracted) * 100
      : (totalLogged > 0 ? 100 : 0);

    return { totalLogged, totalAllocated, totalContracted, completionPct, incompleteCount };
  }, [groupBy, period, teamMemberRows]);

  return {
    rows,
    kpis,
    dayColumns,
    weekColumns,
    weekMondays,
    isLoading,
    refresh,
    userEffectiveTeam,
  };
}
